// START_MODULE pages_news_list_newspage_tsx
// START_CONTRACT
// PURPOSE: Render the paginated archive of community news.
// OPERATIONAL_CONTEXT: Routed at the news hash route and backed by the static MainRepository.
// CONTRACT: Load five items per page, reset scroll immediately on page change and expose bounded previous/next navigation.
// KEYWORDS: news, pagination, static-repository
// COMPLEXITY: 3
// END_CONTRACT
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';

import ArrowRight from 'components/atoms/icons/ArrowRight';
import ArrowLeft from 'components/atoms/icons/ArrowLeft';
import Button from 'components/atoms/button/Button';
import Spinner from 'components/atoms/spinner/Spinner';
import News from 'components/molecules/news/News';

import { INews } from 'domain/entities/INews';
import { mainRepository } from 'domain/repositories/MainRepository';

import Header from 'pages/news/Header';

const NEWS_PER_PAGE = 5;

const NewsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = +(searchParams.get('page') ?? 1);

  const [news, setNews] = useState<INews[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    setNews([]);

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    mainRepository.getAllNews({
      start: (currentPage - 1) * NEWS_PER_PAGE,
      finish: currentPage * NEWS_PER_PAGE,
    })
      .then((news) => {
        setNews(news.news);
        setLoading(false);
        setTotalPages(Math.ceil(news.count / NEWS_PER_PAGE));
      });
  }, [currentPage]);

  const onNext = () => setSearchParams((prev) => ({ ...prev, page: currentPage + 1 }));
  const onPrev = () => setSearchParams((prev) => ({ ...prev, page: currentPage - 1 }));

  return (
    <>
      <Header />

      <NewsWrapper>
        {
          news.map((newsItem) => (
            <News key={newsItem.id} news={newsItem} />
          ))
        }
      </NewsWrapper>

      {
        loading && <Spinner isCentered />
      }

      <PaginationWrapper>
        {
          currentPage > 1 ? (
            <Button onClick={onPrev}>
              <ArrowLeft />
              Предыдущая
            </Button>
          ) : null
        }
        <p>{`${currentPage}/${totalPages}`}</p>
        {
          currentPage !== totalPages ? (
            <Button onClick={onNext}>
              Следующая
              <ArrowRight />
            </Button>
          ) : null
        }
      </PaginationWrapper>
    </>
  );
};

const NewsWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;

  padding: 0 10px;

  >div {
    margin-bottom: 48px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  width: fit-content;
  margin: 0 auto 20px auto;

  >p {
    font-size: 1.4rem;
    font-weight: bold;
  }
`;

export default NewsPage;

// END_MODULE pages_news_list_newspage_tsx
